// components/models/IdeogramInpaintControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import {
  IdeogramInpaintParams,
  IdeogramMagicPrompt,
  IdeogramRenderingSpeed,
  IdeogramStyleType,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';

export const getDefaultIdeogramInpaintParams = (): IdeogramInpaintParams => ({
  prompt: '',
  seed: -1,
  magic_prompt: 'AUTO',
  rendering_speed: 'DEFAULT',
  style_type: 'AUTO',
  num_images: 1,
});

// Sentence-case labels for the UI <-> uppercase enums the Ideogram API expects.
const MAGIC_PROMPT_OPTIONS: { label: string; value: IdeogramMagicPrompt }[] = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'On', value: 'ON' },
  { label: 'Off', value: 'OFF' },
];
const RENDERING_SPEED_OPTIONS: { label: string; value: IdeogramRenderingSpeed }[] = [
  { label: 'Flash (fastest)', value: 'FLASH' },
  { label: 'Turbo', value: 'TURBO' },
  { label: 'Default', value: 'DEFAULT' },
  { label: 'Quality (slowest)', value: 'QUALITY' },
];
const STYLE_TYPE_OPTIONS: { label: string; value: IdeogramStyleType }[] = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'General', value: 'GENERAL' },
  { label: 'Realistic', value: 'REALISTIC' },
  { label: 'Design', value: 'DESIGN' },
  { label: 'Fiction', value: 'FICTION' },
];

const MAGIC_PROMPT_LABELS = MAGIC_PROMPT_OPTIONS.map(o => o.label);
const RENDERING_SPEED_LABELS = RENDERING_SPEED_OPTIONS.map(o => o.label);
const STYLE_TYPE_LABELS = STYLE_TYPE_OPTIONS.map(o => o.label);

const FULL_WIDTH_STYLE: React.CSSProperties = { width: '100%' };

interface IdeogramInpaintControlPanelProps {
  params: IdeogramInpaintParams;
  setModelParams: (
    updater: (prevParams: { [model: string]: IdeogramInpaintParams }) => { [model: string]: IdeogramInpaintParams }
  ) => void;
  diffusionModel: string;
}

const IdeogramInpaintControlPanel: React.FC<IdeogramInpaintControlPanelProps> = ({
  params, setModelParams, diffusionModel
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback((name: keyof IdeogramInpaintParams, value: unknown) => {
    setModelParams(prevModelParams => {
      // Fall back to defaults so the first edit always works even if params weren't initialized.
      const currentParams = prevModelParams[diffusionModel] ?? getDefaultIdeogramInpaintParams();
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: typeof value === 'string' ? undefined : value,
      value_length: typeof value === 'string' ? value.length : undefined,
      model_name: diffusionModel,
      source_component: 'IdeogramInpaintControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleTextChange = useCallback((
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    updateParam(e.target.name as keyof IdeogramInpaintParams, e.target.value);
  }, [updateParam]);

  const handleMagicPromptChange = useCallback((label: string) => {
    updateParam('magic_prompt', MAGIC_PROMPT_OPTIONS.find(o => o.label === label)?.value ?? 'AUTO');
  }, [updateParam]);

  const handleRenderingSpeedChange = useCallback((label: string) => {
    updateParam('rendering_speed', RENDERING_SPEED_OPTIONS.find(o => o.label === label)?.value ?? 'DEFAULT');
  }, [updateParam]);

  const handleStyleTypeChange = useCallback((label: string) => {
    updateParam('style_type', STYLE_TYPE_OPTIONS.find(o => o.label === label)?.value ?? 'AUTO');
  }, [updateParam]);

  return (
    <>
      <div className="mb-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-neutral-400">
        Paint the area to change with the Mask tool, then describe the result below.
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what should appear in the masked area..."
          description="What to generate inside the painted region."
          name="prompt"
          value={params.prompt ?? ''}
          onChange={handleTextChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="ideogramInpaintMagicPrompt"
          label="Magic Prompt"
          value={MAGIC_PROMPT_OPTIONS.find(o => o.value === params.magic_prompt)?.label ?? 'Auto'}
          options={MAGIC_PROMPT_LABELS}
          onChange={handleMagicPromptChange}
          style={FULL_WIDTH_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Let Ideogram expand and refine your prompt automatically.</p>
      </div>

      <div className="mb-4">
        <Select
          id="ideogramInpaintRenderingSpeed"
          label="Rendering Speed"
          value={RENDERING_SPEED_OPTIONS.find(o => o.value === params.rendering_speed)?.label ?? 'Default'}
          options={RENDERING_SPEED_LABELS}
          onChange={handleRenderingSpeedChange}
          style={FULL_WIDTH_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Trades generation speed against final image quality.</p>
      </div>

      <div className="mb-4">
        <Select
          id="ideogramInpaintStyleType"
          label="Style Type"
          value={STYLE_TYPE_OPTIONS.find(o => o.value === params.style_type)?.label ?? 'Auto'}
          options={STYLE_TYPE_LABELS}
          onChange={handleStyleTypeChange}
          style={FULL_WIDTH_STYLE}
        />
      </div>
    </>
  );
};

export default memo(IdeogramInpaintControlPanel);
